import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

import de from './locales/de.json';
import en from './locales/en.json';
import uk from './locales/uk.json';

export const LANGUAGES = ['de', 'en', 'uk'] as const;
export type Language = (typeof LANGUAGES)[number];
const STORAGE_KEY = 'wg.language';

export const isLanguage = (value: unknown): value is Language =>
  typeof value === 'string' && (LANGUAGES as readonly string[]).includes(value);

/** The supported language for an i18next language tag ('uk-UA' → 'uk'); German otherwise. */
export function toLanguage(tag: string | undefined): Language {
  const base = (tag ?? '').toLowerCase().split('-')[0];
  return isLanguage(base) ? base : 'de';
}

/** Intl locale for dates, numbers and prices. */
const INTL_LOCALES: Record<Language, string> = { de: 'de-DE', en: 'en-GB', uk: 'uk-UA' };
export const intlLocale = (language: string) => INTL_LOCALES[toLanguage(language)];

function initialLanguage(): Language {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (isLanguage(stored)) return stored;
  } catch {
    // storage blocked: fall back to the browser language
  }
  const browser = typeof navigator !== 'undefined' ? navigator.language.toLowerCase() : '';
  if (browser.startsWith('en')) return 'en';
  if (browser.startsWith('uk')) return 'uk';
  return 'de';
}

void i18n.use(initReactI18next).init({
  resources: { de: { translation: de }, en: { translation: en }, uk: { translation: uk } },
  lng: initialLanguage(),
  supportedLngs: LANGUAGES,
  nonExplicitSupportedLngs: true,
  fallbackLng: 'de',
  interpolation: {
    escapeValue: false, // React escapes
    // The public brand is configurable (BRAND_NAME); texts say {{brand}}, never the name itself.
    defaultVariables: { brand: __BRAND_NAME__, verifyPrefix: __VERIFY_PREFIX__ },
  },
  returnNull: false,
});

export function setLanguage(language: Language) {
  void i18n.changeLanguage(language);
  try {
    localStorage.setItem(STORAGE_KEY, language);
  } catch {
    // not persisted: fine
  }
}

// <html lang> follows the UI language, however it changes (switcher, profile, tests).
const syncHtmlLang = (language: string) => {
  if (typeof document !== 'undefined') document.documentElement.lang = toLanguage(language);
};
i18n.on('languageChanged', syncHtmlLang);
syncHtmlLang(i18n.language);

/** 24.09.2026, 14:05 in the account's time zone (PRD §6). */
export function formatDateTime(iso: string, language: string, timeZone?: string) {
  return new Intl.DateTimeFormat(intlLocale(language), {
    dateStyle: 'medium',
    timeStyle: 'short',
    ...(timeZone ? { timeZone } : {}),
  }).format(new Date(iso));
}

export function formatDate(iso: string, language: string, timeZone?: string) {
  return new Intl.DateTimeFormat(intlLocale(language), {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    ...(timeZone ? { timeZone } : {}),
  }).format(new Date(iso));
}

/** 14:05 in the account's time zone. */
export function formatTime(iso: string, language: string, timeZone?: string) {
  return new Intl.DateTimeFormat(intlLocale(language), {
    hour: '2-digit',
    minute: '2-digit',
    ...(timeZone ? { timeZone } : {}),
  }).format(new Date(iso));
}

/** Whether two moments fall on the same calendar day in the time zone. */
export function sameDay(a: Date, b: Date, timeZone?: string) {
  const day = new Intl.DateTimeFormat('en-CA', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    ...(timeZone ? { timeZone } : {}),
  });
  return day.format(a) === day.format(b);
}

/** "vor 48 Sek.", "vor 3 Tagen": how long ago, in the largest whole unit (overview.html). */
export function formatAgo(iso: string, language: string, now = Date.now()) {
  const seconds = Math.max(0, Math.round((now - new Date(iso).getTime()) / 1000));
  const ago = new Intl.RelativeTimeFormat(intlLocale(language), {
    numeric: 'always',
    style: 'short',
  });
  if (seconds < 60) return ago.format(-seconds, 'second');
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return ago.format(-minutes, 'minute');
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return ago.format(-hours, 'hour');
  return ago.format(-Math.floor(hours / 24), 'day');
}

export function formatNumber(value: number, language: string, digits = 1) {
  return new Intl.NumberFormat(intlLocale(language), {
    maximumFractionDigits: digits,
  }).format(value);
}

export function formatDuration(seconds: number, language: string) {
  const minutes = Math.max(1, Math.round(seconds / 60));
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  const lang = toLanguage(language);
  if (lang === 'en') return h > 0 ? `${h} h ${m} min` : `${m} min`;
  if (lang === 'uk') return h > 0 ? `${h} год ${m} хв` : `${m} хв`;
  return h > 0 ? `${h} Std. ${m} Min.` : `${m} Min.`;
}

export default i18n;
